import Image from 'next/image'
import {FC} from 'react'

import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {Text, TextFontWeight, TextVariant} from '../common/content/Text'
import {QuoteIcon} from '../common/icons/QuoteIcon'

type QuoteProps = {
  photoSrc: string
  photoAlt: string
  quote: string
  author: string
  role: string
  backgroundColor?: string
}

export const Quote: FC<React.PropsWithChildren<QuoteProps>> = ({
  photoSrc,
  photoAlt,
  quote,
  author,
  role,
  backgroundColor = theme.palette.background.butter,
}) => {
  return (
    <div className="wrapper">
      <div className="image-mask">
        <div className="image-wrapper">
          <Image
            src={photoSrc}
            alt={photoAlt}
            style={{objectFit: 'cover'}}
            fill
            sizes={`${MOBILE_SCREENSIZE} 80px, ${TABLET_SCREENSIZE} 160px, 350px`}
          />
        </div>
      </div>
      <div className="content">
        <div className="quote">
          <div className="quote-icon">
            <QuoteIcon />
          </div>
          <Text variant={TextVariant.text24} fontWeight={TextFontWeight.light} element="blockquote">
            {quote}
          </Text>
        </div>
        <div className="author">
          <Text variant={TextVariant.h4} element="p">
            {author}
          </Text>
          <div className="role">
            <Text color={theme.palette.text.light}>{role}</Text>
          </div>
        </div>
      </div>

      <style jsx>{`
        .wrapper {
          display: flex;
        }
        .image-mask {
          overflow: hidden;
          width: 350px;
          height: 350px;
          flex: 0 0 350px;
          border-radius: 16px;
        }
        .image-wrapper {
          position: relative;
          width: 100%;
          height: 100%;
        }
        .content {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }
        .quote {
          display: flex;
          background-color: ${backgroundColor};
          padding: ${theme.spacing[6]} ${theme.spacing[5]};
          margin-left: -${theme.spacing[6]};
          position: relative;
          z-index: 1;
          border-radius: 16px;
        }
        .quote-icon {
          margin-top: -${theme.spacing[1]};
          margin-right: ${theme.spacing[4]};
        }
        .author {
          margin-left: ${theme.spacing[5]};
        }
        .role {
          margin-top: ${theme.spacing[2]};
        }
        @media ${TABLET_SCREENSIZE} {
          .wrapper {
            flex-direction: column;
            align-items: center;
            background-color: ${backgroundColor};
            padding: ${theme.spacing[6]};
            border-radius: 16px;
          }
          .image-mask {
            width: 160px;
            height: 160px;
            flex: 0 0 160px;
          }
          .quote {
            flex-direction: column;
            align-items: center;
            text-align: center;
            padding: 0;
            margin-left: 0;
            margin-top: ${theme.spacing[5]};
          }
          .quote-icon {
            margin-top: 0;
            margin-right: 0;
            margin-bottom: ${theme.spacing[4]};
          }
          .author {
            text-align: center;
            margin-top: ${theme.spacing[5]};
            margin-left: 0;
          }
          .role {
            margin-top: 0;
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .wrapper {
            padding: ${theme.spacing[6]} ${theme.spacing[3]};
          }
          .image-mask {
            width: 80px;
            height: 80px;
            flex: 0 0 80px;
          }
          .quote {
            margin-top: ${theme.spacing[3]};
          }
          .quote-icon {
            margin-bottom: ${theme.spacing[3]};
          }
          .author {
            margin-top: ${theme.spacing[3]};
          }
        }
      `}</style>
    </div>
  )
}
